import { API_URL, getAuthHeaders } from './config';

// Re-export User type if needed
export const usersService = {
    getStats: async (id: string) => {
        const res = await fetch(`${API_URL}/users/${id}/stats`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch user stats");
        return res.json();
    },
    getMe: async () => {
        const res = await fetch(`${API_URL}/users/me`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch profile");
        const user = await res.json();
        // Fix relative URL
        if (user.profile_pic_url && user.profile_pic_url.startsWith('/')) {
            const baseUrl = API_URL.replace(/\/api$/, '');
            user.profile_pic_url = `${baseUrl}${user.profile_pic_url}`;
        }
        return user;
    },
    get: async (id: string) => {
        const res = await fetch(`${API_URL}/users/${id}`);
        if (!res.ok) throw new Error("Failed to fetch user");
        const user = await res.json();
        // Fix relative URL to absolute for images
        if (user.profile_pic_url && user.profile_pic_url.startsWith('/')) {
            const baseUrl = API_URL.replace(/\/api$/, '');
            user.profile_pic_url = `${baseUrl}${user.profile_pic_url}`;
        }
        return user;
    },
    search: async (query: string) => {
        const res = await fetch(`${API_URL}/users/search?q=${encodeURIComponent(query)}`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Search failed");
        const data = await res.json();
        const users = Array.isArray(data) ? data : (data.results || []);
        const baseUrl = API_URL.replace(/\/api$/, '');
        return (users as any[]).map(u => {
            if (u.profile_pic_url && u.profile_pic_url.startsWith('/')) {
                u.profile_pic_url = `${baseUrl}${u.profile_pic_url}`;
            }
            return u;
        });
    },
    uploadPhoto: async (id: string, file: Blob) => {
        const res = await fetch(`${API_URL}/users/${id}/photo`, {
            method: 'POST',
            headers: {
                'Content-Type': 'image/jpeg',
                ...getAuthHeaders()
            },
            body: file
        });
        if (!res.ok) throw new Error("Upload failed");
        const data = await res.json();
        if (data.url && data.url.startsWith('/')) {
            const baseUrl = API_URL.replace(/\/api$/, '');
            data.url = `${baseUrl}${data.url}`;
        }
        return data;
    },
    uploadIdProof: async (id: string, file: Blob) => {
        const res = await fetch(`${API_URL}/users/${id}/upload-proof`, {
            method: 'POST',
            headers: {
                'Content-Type': 'image/jpeg',
                ...getAuthHeaders()
            },
            body: file
        });
        if (!res.ok) throw new Error("Upload failed");
        const data = await res.json();
        if (data.url && data.url.startsWith('/')) {
            const baseUrl = API_URL.replace(/\/api$/, '');
            data.url = `${baseUrl}${data.url}`;
        }
        return data;
    },
    updateLevel: async (id: string, level: number) => {
        const res = await fetch(`${API_URL}/users/${id}/level`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ level })
        });
        if (!res.ok) throw new Error("Failed to update level");
        return res.json();
    },
    update: async (id: string, data: any) => {
        const res = await fetch(`${API_URL}/users/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            throw new Error(errorData.error || errorData.message || "Failed to update profile");
        }
        return res.json();
    },
    delete: async (id: string) => {
        const res = await fetch(`${API_URL}/users/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to delete account");
        return res.json();
    },
    getClaims: async (id: string) => {
        const res = await fetch(`${API_URL}/users/${id}/claims`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch claims");
        return res.json();
    },
    async getRatingHistory(userId: string): Promise<any[]> {
        const response = await fetch(`${API_URL}/users/${userId}/rating-history`, {
            headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error("Erreur historique");
        return response.json();
    },

    async exportData(userId: string): Promise<any> {
        const response = await fetch(`${API_URL}/users/${userId}/export`, {
            headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error("Erreur lors de l'exportation des données");
        return response.json();
    },
    async listPasskeys(): Promise<any[]> {
        const response = await fetch(`${API_URL}/auth/webauthn/list`, {
            headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error("Failed to list passkeys");
        return response.json();
    },
    async deletePasskey(id: string): Promise<any> {
        const response = await fetch(`${API_URL}/auth/webauthn/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error("Failed to delete passkey");
        return response.json();
    },
    async markManagerOnboardingDone(): Promise<any> {
        const response = await fetch(`${API_URL}/auth/onboarding/manager-done`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error("Failed to mark onboarding done");
        return response.json();
    },
    updateLocation: async (data: { location_sharing_enabled: boolean; latitude?: number | null; longitude?: number | null }) => {
        const res = await fetch(`${API_URL}/users/location`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update location");
        return res.json();
    },
    getActiveLocations: async () => {
        const res = await fetch(`${API_URL}/users/active-locations`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch active locations");
        const users = await res.json();
        const baseUrl = API_URL.replace(/\/api$/, '');
        return (users as any[]).map(u => {
            if (u.profile_pic_url && u.profile_pic_url.startsWith('/')) {
                u.profile_pic_url = `${baseUrl}${u.profile_pic_url}`;
            }
            return u;
        });
    }
};
